import React, { useState, useRef, useEffect } from 'react'
import IconButton from '@mui/material/IconButton'
import MicIcon from '@mui/icons-material/Mic'
import RefreshIcon from '@mui/icons-material/Refresh'
import Info from '@mui/icons-material/Info'
import WarningAmberIcon from '@mui/icons-material/Warning'

import Button from '@mui/material/Button'
import { uploadSpecificFile } from '../../core/services/api'
import {
  createTranscription,
  getTranscriptionDetail,
} from '../../core/services/api/aiServices'

interface AITranscriptionProps {
  onTranscriptionComplete: (transcription: string) => void
  onReRecord?: () => void
  disabled?: boolean
  fieldName: string
  patientId?: string | number
  patientName?: string
  triggeredByUserId?: string | number
}

const AITranscription: React.FC<AITranscriptionProps> = ({
  onTranscriptionComplete,
  onReRecord,
  disabled = false,
  fieldName,
  patientId,
  patientName,
  triggeredByUserId,
}) => {
  const [isRecording, setIsRecording] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [recordingTime, setRecordingTime] = useState(0)
  const [transcriptionComplete, setTranscriptionComplete] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const pollingRef = useRef<NodeJS.Timeout | null>(null)
  const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
      if (pollingRef.current) clearInterval(pollingRef.current)
      if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current)
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state === 'recording'
      ) {
        mediaRecorderRef.current.stop()
      }
    }
  }, [])

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  const startTimer = () => {
    timerRef.current = setInterval(() => {
      setRecordingTime((prev) => {
        const newTime = prev + 1

        if (newTime >= 15 * 60) {
          pauseRecording()
        }
        return newTime
      })
    }, 1000)
  }

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
  }

  const downloadRecording = (audioBlob: Blob) => {
    const url = URL.createObjectURL(audioBlob)
    const link = document.createElement('a')
    link.href = url
    link.download = `transcription-${Date.now()}.webm`
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  }

  const getPreferredMimeType = (): string | undefined => {
    const candidates = [
      'audio/wav',
      'audio/mpeg',
      'audio/webm;codecs=opus',
      'audio/webm',
    ]
    for (const mimeType of candidates) {
      if (MediaRecorder.isTypeSupported(mimeType)) {
        return mimeType
      }
    }
    return undefined
  }

  const startRecording = async () => {
    try {
      setErrorMessage('')
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })

      const preferredMimeType = getPreferredMimeType()
      mediaRecorderRef.current = new MediaRecorder(
        stream,
        preferredMimeType ? { mimeType: preferredMimeType } : undefined,
      )
      chunksRef.current = []

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data)
        }
      }

      mediaRecorderRef.current.onstop = async () => {
        const blobType = preferredMimeType || 'audio/webm'
        const audioBlob = new Blob(chunksRef.current, { type: blobType })
        stream.getTracks().forEach((track) => track.stop())
        // If ever needed
        // downloadRecording(audioBlob)
        setIsProcessing(true)
        await callTranscriptionAPI(audioBlob)
        setRecordingTime(0)
      }

      mediaRecorderRef.current.start()
      setIsRecording(true)
      setIsPaused(false)
      setTranscriptionComplete(false)
      startTimer()
    } catch (error) {
      console.error('Error accessing microphone:', error)
      alert('Unable to access microphone. Please check permissions.')
    }
  }

  const pauseRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === 'recording'
    ) {
      mediaRecorderRef.current.pause()
      setIsPaused(true)
      stopTimer()
    }
  }

  const resumeRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === 'paused'
    ) {
      mediaRecorderRef.current.resume()
      setIsPaused(false)
      startTimer()
    }
  }

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop()
      setIsRecording(false)
      setIsPaused(false)
      stopTimer()
    }
  }

  const handleReRecord = () => {
    setShowDeleteConfirm(true)
  }

  const confirmReRecord = () => {
    setShowDeleteConfirm(false)
    setTranscriptionComplete(false)
    setErrorMessage('')
    onReRecord?.()
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
    startRecording()
  }

  const getAudioExtension = (mimeType: string): string => {
    if (mimeType.includes('audio/wav')) return 'wav'
    if (mimeType.includes('audio/mpeg') || mimeType.includes('audio/mp3'))
      return 'mp3'
    if (mimeType.includes('audio/mp4') || mimeType.includes('audio/m4a'))
      return 'm4a'
    if (mimeType.includes('audio/webm')) return 'webm'
    return 'audio'
  }

  const uploadAudioForTranscription = async (
    audioBlob: Blob,
  ): Promise<string> => {
    try {
      let mimeType = audioBlob.type || 'audio/webm'
      if (mimeType.startsWith('audio/webm')) {
        mimeType = 'audio/webm'
      }
      const extension = getAudioExtension(mimeType)
      const file = new File(
        [new Blob([audioBlob], { type: mimeType })],
        `transcription-${Date.now()}.${extension}`,
        {
          type: mimeType,
        },
      )
      const formData = new FormData()
      formData.append('files', file)

      const uploadedFiles = await uploadSpecificFile(formData, true)
      return uploadedFiles?.[0]
    } catch (error: any) {
      throw error
    }
  }

  const showError = (message: string) => {
    setErrorMessage(message)
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current)
    }
    errorTimeoutRef.current = setTimeout(() => {
      setErrorMessage('')
    }, 3000)
  }

  const handleTranscriptionResult = (response: any) => {
    const transcriptionText =
      response?.summary ||
      // response?.clinical_notes ||
      // response?.transcription_srt ||
      // response?.original_transcription_srt ||
      ''

    if (transcriptionText) {
      onTranscriptionComplete(transcriptionText)
    }
  }

  const startPollingTranscription = (id: number) => {
    if (pollingRef.current) clearInterval(pollingRef.current)

    pollingRef.current = setInterval(async () => {
      try {
        const detail = await getTranscriptionDetail(id)
        const state = detail?.state
        if (state === 'COMPLETED') {
          handleTranscriptionResult(detail)
          setTranscriptionComplete(true)
          setIsProcessing(false)
          if (pollingRef.current) {
            clearInterval(pollingRef.current)
            pollingRef.current = null
          }
        } else if (state === 'ERROR') {
          setIsProcessing(false)
          setTranscriptionComplete(false)
          showError(detail?.error_message || 'Transcription failed')
          if (pollingRef.current) {
            clearInterval(pollingRef.current)
            pollingRef.current = null
          }
        }
      } catch (error) {
        setIsProcessing(false)
        setTranscriptionComplete(false)
        showError('Unable to fetch transcription status')
        if (pollingRef.current) {
          clearInterval(pollingRef.current)
          pollingRef.current = null
        }
      }
    }, 5000)
  }

  const callTranscriptionAPI = async (audioBlob: Blob): Promise<void> => {
    try {
      const audioUrl = await uploadAudioForTranscription(audioBlob)
      const response = await createTranscription({
        audio_file: audioUrl,
        title: fieldName,
        patient_id: patientId,
        patient_name: patientName,
        triggered_by_user_id: triggeredByUserId,
      })

      if (response?.state === 'ERROR') {
        showError(response?.error_message || 'Transcription failed')
        setIsProcessing(false)
        setTranscriptionComplete(false)
        return
      }

      if (response?.id) {
        startPollingTranscription(response.id)
        return
      }

      handleTranscriptionResult(response)
      setIsProcessing(false)
      setTranscriptionComplete(true)
    } catch (error: any) {
      showError(
        error?.response?.data?.error_message ||
        error?.message ||
        'Transcription failed',
      )
      setIsProcessing(false)
      setTranscriptionComplete(false)
    }
  }

  // Initial state - show mic button
  if (!isRecording && !isProcessing && !transcriptionComplete) {
    return (
      <div className='mb-4'>
        {errorMessage && (
          <div className='alert alert-danger py-2 mb-2' role='alert'>
            <small>{errorMessage}</small>
          </div>
        )}
        <div
          className='flex items-center py-3 transition-all cursor-pointer'
          onClick={!disabled ? startRecording : undefined}
        >
          <IconButton
            disabled={disabled}
            sx={{
              marginRight: '12px',
              backgroundColor: '#1976d2',
              color: 'white',
              '&:hover': {
                backgroundColor: '#1565c0',
              },
              '&.Mui-disabled': {
                backgroundColor: '#bdbdbd',
                color: 'white',
              },
            }}
          >
            <MicIcon />
          </IconButton>
          <span className='text-sm font-medium text-gray-700'>
            Click to start AI transcription
          </span>
        </div>
      </div>
    )
  }

  // After transcription complete - show Re-record button
  if (transcriptionComplete && !showDeleteConfirm) {
    return (
      <div className='mb-4'>
        <div className='flex items-center py-3'>
          <IconButton
            onClick={handleReRecord}
            sx={{
              marginRight: '12px',
              backgroundColor: '#1976d2',
              color: 'white',
              '&:hover': {
                backgroundColor: '#1565c0',
              },
            }}
          >
            <RefreshIcon />
          </IconButton>
          <span className='text-sm font-medium text-gray-700'>Re-record</span>
        </div>
        <div
          className='flex items-center gap-2 p-3 mb-4'
          style={{
            backgroundColor: '#d4edda',
            border: '1px solid #c3e6cb',
            borderRadius: '4px',
          }}
        >
          <svg
            width='20'
            height='20'
            viewBox='0 0 24 24'
            fill='none'
            stroke='#155724'
            strokeWidth='2'
          >
            <polyline points='20 6 9 17 4 12'></polyline>
          </svg>
          <span className='text-sm font-medium' style={{ color: '#155724' }}>
            Transcription Completed!
          </span>
        </div>
      </div>
    )
  }

  // Show delete confirmation
  if (showDeleteConfirm) {
    return (
      <div className='mb-4'>
        <div className='flex items-center py-3'>
          <IconButton
            sx={{
              marginRight: '12px',
              backgroundColor: '#93c5fd',
              color: 'white',
            }}
            disabled
          >
            <RefreshIcon />
          </IconButton>
          <span className='text-sm font-medium text-gray-700'>Re-record</span>
        </div>
        <div
          className='flex items-center justify-between p-3 mb-4'
          style={{
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '4px',
          }}
        >
          <div className='flex items-center gap-2'>
            <span className='text-lg ' style={{ color: '#E17100' }}>
              <WarningAmberIcon />
            </span>
            <span className='text-sm font-medium' style={{ color: '#856404' }}>
              Current transcript will be deleted. Continue?
            </span>
          </div>
          <div className='flex gap-2 mt-3'>
            <Button
              onClick={confirmReRecord}
              variant='outlined'
              size='small'
              sx={{
                textTransform: 'none',
                borderColor: '#856404',
                color: '#856404',
                '&:hover': {
                  borderColor: '#856404',
                  backgroundColor: '#fff3cd',
                },
              }}
            >
              Yes, Continue
            </Button>
            <Button
              onClick={() => setShowDeleteConfirm(false)}
              variant='text'
              size='small'
              sx={{
                textTransform: 'none',
                color: '#666',
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // Processing state
  if (isProcessing) {
    return (
      <div className='flex items-center gap-4 py-3 mb-4'>
        <img
          src='/assets/images/transcription_processing.svg'
          alt='Processing'
          style={{ width: '60px', height: '24px' }}
        />
        <span className='text-sm font-medium' style={{ color: '#1976d2' }}>
          Processing transcription...
        </span>
      </div>
    )
  }

  return (
    <div className='mb-4'>
      <div
        className='py-3'
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexWrap: 'nowrap',
          whiteSpace: 'nowrap',
        }}
      >
        <span className='text-sm font-medium' style={{ color: '#1976d2' }}>
          AI is listening...
        </span>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <img
            src='/assets/images/transcription_processing.svg'
            alt='Listening'
            style={{ width: '60px', height: '24px' }}
          />
        </div>
        <span
          className='font-mono text-sm font-semibold'
          style={{ margin: 0, border: 'none' }}
        >
          {formatTime(recordingTime)}
        </span>
        <div
          style={{
            width: '0px',
            height: '33px',
            borderLeft: '1px solid #d0d0d0',
            opacity: 1,
            margin: '0px 20px',
          }}
        />
        {isPaused ? (
          <Button
            onClick={resumeRecording}
            size='small'
            startIcon={
              <img
                src='/assets/images/transcription_play.svg'
                alt='Resume'
                style={{ width: '25px', height: '25px' }}
              />
            }
            style={{
              textTransform: 'none',
              backgroundColor: 'transparent',
            }}
          >
            Resume
          </Button>
        ) : (
          <Button
            onClick={pauseRecording}
            size='small'
            startIcon={
              <img
                src='/assets/images/transcription_pause.svg'
                alt='Pause'
                style={{ width: '25px', height: '25px' }}
              />
            }
            style={{
              backgroundColor: 'transparent',
              color: '#000',
              textTransform: 'none',
            }}
          >
            Pause
          </Button>
        )}
        <Button
          onClick={stopRecording}
          size='small'
          style={{
            backgroundColor: '#dc3545',
            color: '#fff',
            textTransform: 'none',
          }}
        >
          Stop Recording
        </Button>
      </div>
      {(isRecording || isPaused) && (
        <div
          className='flex items-center justify-between p-3 mb-4'
          style={{
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '4px',
          }}
        >
          <div className='flex items-center gap-2'>
            <span className='text-lg ' style={{ color: '#E17100' }}>
              <Info />
            </span>
            <span className='text-sm font-medium' style={{ color: '#856404' }}>
              {isPaused
                ? 'Recording paused. Resume to continue.'
                : 'Recording pauses after 15 minutes. Resume to Continue.'}{' '}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

export default AITranscription
